import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { HolidaysEditor } from "@/components/HolidaysEditor";
import { SignInLinkCard } from "@/components/SignInLinkCard";
import { ReferenceListEditor } from "@/components/ReferenceListEditor";
import { Save, Plus, Trash2, Image as ImageIcon, X, Wand2, Mail, Send } from "lucide-react";
import { MoneyInput } from "@/components/MoneyInput";

const FILE_LIMIT = 800 * 1024; // 800 KB max for logo data URL

/**
 * Extract the dominant non-neutral colour from an image data URL.
 * Samples on a 64×64 canvas, buckets pixels by 32-step quantisation, ignores
 * near-white / near-black / near-grey / fully-transparent pixels, returns the
 * most common bucket as a #RRGGBB hex string.
 */
function extractDominantColor(dataUrl) {
    return new Promise((resolve, reject) => {
        if (!dataUrl) return resolve(null);
        // SVG is fine to draw to canvas in modern browsers, but data URI parsing
        // can be flaky — ignore SVG and let the user pick a colour manually.
        if (dataUrl.startsWith("data:image/svg")) return resolve(null);
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
            try {
                const SIZE = 64;
                const canvas = document.createElement("canvas");
                canvas.width = SIZE; canvas.height = SIZE;
                const ctx = canvas.getContext("2d", { willReadFrequently: true });
                ctx.drawImage(img, 0, 0, SIZE, SIZE);
                const { data } = ctx.getImageData(0, 0, SIZE, SIZE);
                const buckets = new Map();
                const Q = 32; // 32-step quantisation
                for (let i = 0; i < data.length; i += 4) {
                    const a = data[i + 3];
                    if (a < 200) continue; // skip transparent
                    const r = data[i], g = data[i + 1], b = data[i + 2];
                    // skip near-white
                    if (r > 240 && g > 240 && b > 240) continue;
                    // skip near-black
                    if (r < 20 && g < 20 && b < 20) continue;
                    // skip near-grey (low saturation)
                    const max = Math.max(r, g, b), min = Math.min(r, g, b);
                    const sat = max === 0 ? 0 : (max - min) / max;
                    if (sat < 0.18) continue;
                    const key = `${Math.floor(r / Q)},${Math.floor(g / Q)},${Math.floor(b / Q)}`;
                    const bucket = buckets.get(key) || { r: 0, g: 0, b: 0, n: 0 };
                    bucket.r += r; bucket.g += g; bucket.b += b; bucket.n += 1;
                    buckets.set(key, bucket);
                }
                if (buckets.size === 0) return resolve(null);
                let best = null;
                for (const v of buckets.values()) {
                    if (!best || v.n > best.n) best = v;
                }
                if (!best) return resolve(null);
                const r = Math.round(best.r / best.n);
                const g = Math.round(best.g / best.n);
                const b = Math.round(best.b / best.n);
                const toHex = (x) => x.toString(16).padStart(2, "0");
                resolve(`#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase());
            } catch (e) {
                reject(e);
            }
        };
        img.onerror = () => reject(new Error("could not load image"));
        img.src = dataUrl;
    });
}

const Field = ({ label, children, hint, span = 1 }) => (
    <div className={span === 2 ? "md:col-span-2" : span === 3 ? "md:col-span-3" : ""}>
        <label className="overline block mb-2">{label}</label>
        {children}
        {hint && <p className="text-[11px] text-ink/40 mt-1">{hint}</p>}
    </div>
);

const inputCls = "w-full border border-ink/15 py-2.5 px-3 text-sm focus:border-klein focus:outline-none";

export default function Settings() {
    const navigate = useNavigate();
    const [form, setForm] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [pfaList, setPfaList] = useState([]);
    const [official, setOfficial] = useState({ banks: [], pfas: [], hmos: [] });

    useEffect(() => {
        (async () => {
            try {
                const [{ data }, { data: pf }, { data: bk }, { data: hm }] = await Promise.all([
                    api.get("/company-settings"),
                    api.get("/reference/pfas").catch(() => ({ data: { pfas: [] } })),
                    api.get("/reference/banks").catch(() => ({ data: { banks: [] } })),
                    api.get("/reference/hmos").catch(() => ({ data: { hmos: [] } })),
                ]);
                setForm(data);
                setPfaList(pf?.pfas || []);
                setOfficial({ banks: bk?.official || [], pfas: pf?.official || [], hmos: hm?.official || [] });
            } catch (err) {
                console.error("Could not load company settings:", err);
                toast.error("Could not load settings");
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    // Update one cell inside `extra_salary_components[idx]` without mutating
    // state. Keeps the JSX terse where there are 7 fields per row.
    const setExtra = (idx, key, value) => {
        setForm((f) => {
            const next = [...(f.extra_salary_components || [])];
            next[idx] = { ...next[idx], [key]: value };
            return { ...f, extra_salary_components: next };
        });
    };

    const onLogoUpload = (file) => {
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            toast.error("Please pick an image (PNG, JPG, SVG, WebP).");
            return;
        }
        if (file.size > FILE_LIMIT) {
            toast.error("Logo too large — keep it under 800 KB.");
            return;
        }
        const reader = new FileReader();
        reader.onload = async (e) => {
            const dataUrl = e.target.result;
            set("logo_data_url", dataUrl);
            // Extract dominant color from the logo
            try {
                const dominant = await extractDominantColor(dataUrl);
                if (dominant) {
                    setForm((f) => ({ ...f, logo_data_url: dataUrl, primary_color: dominant }));
                    toast.success(`Brand colour updated from logo: ${dominant}`);
                }
            } catch (err) {
                // Non-fatal — keep user's current primary_color and log for diagnostics
                console.warn("Logo colour extraction failed:", err);
            }
        };
        reader.readAsDataURL(file);
    };

    const onSignatureUpload = (file) => {
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            toast.error("Please pick an image (PNG, JPG, WebP). SVG is not supported in PDFs.");
            return;
        }
        if (file.type === "image/svg+xml") {
            toast.error("SVG signatures don't render in PDFs — please upload a PNG or JPG instead.");
            return;
        }
        if (file.size > FILE_LIMIT) {
            toast.error("Signature too large — keep it under 800 KB.");
            return;
        }
        const reader = new FileReader();
        reader.onload = (e) => set("hr_signature_data_url", e.target.result);
        reader.readAsDataURL(file);
    };

    const onLetterheadFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 2 * 1024 * 1024) {
            toast.error("Letterhead too large — keep it under 2 MB.");
            return;
        }
        const reader = new FileReader();
        reader.onload = (ev) => set("letterhead_data_url", ev.target.result);
        reader.readAsDataURL(file);
    };

    const totalWeight = useMemo(() => {
        if (!form?.performance_criteria) return 0;
        return form.performance_criteria.reduce((s, c) => s + (Number(c.weight) || 0), 0);
    }, [form]);

    // Salary structure — DYNAMIC list of {key,label,percent,pensionable,taxable}
    const structure = useMemo(() => {
        const s = form?.salary_structure;
        if (Array.isArray(s) && s.length) return s;
        // Legacy hydrate from basic_pct/housing_pct/transport_pct if structure missing
        return [
            { key: "basic_salary", label: "Basic Salary", percent: Math.round(((form?.basic_pct ?? 0.60) * 100) * 10) / 10, pensionable: true, taxable: true, is_basic: true },
            { key: "housing", label: "Housing Allowance", percent: Math.round(((form?.housing_pct ?? 0.25) * 100) * 10) / 10, pensionable: false, taxable: true },
            { key: "transport", label: "Transport Allowance", percent: Math.round(((form?.transport_pct ?? 0.15) * 100) * 10) / 10, pensionable: false, taxable: true },
        ];
    }, [form?.salary_structure, form?.basic_pct, form?.housing_pct, form?.transport_pct]);

    const salaryTotalUi = useMemo(
        () => Math.round(structure.reduce((s, r) => s + (Number(r.percent) || 0), 0) * 10) / 10,
        [structure],
    );

    const setStructureRow = (idx, key, value) => {
        setForm((f) => {
            const arr = [...(f.salary_structure || structure)];
            const prevPct = Number(arr[idx]?.percent) || 0;
            const isBasic = !!arr[idx]?.is_basic;
            arr[idx] = { ...arr[idx], [key]: key === "percent" ? Math.max(0, Math.min(100, Number(value) || 0)) : value };
            if (key === "label" && !arr[idx].is_basic) {
                arr[idx].key = String(value || "").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
            }
            // Auto-keep total = 100 by borrowing/returning against Basic Salary when a
            // non-basic row's percent changes. User can add, remove and edit allowances
            // without manually rebalancing Basic every time.
            if (key === "percent" && !isBasic) {
                const newPct = Number(arr[idx].percent) || 0;
                const delta = newPct - prevPct;
                const basicIdx = arr.findIndex((r) => r.is_basic || r.key === "basic_salary");
                if (basicIdx >= 0) {
                    const basicPct = Number(arr[basicIdx].percent) || 0;
                    const nextBasic = Math.max(0, Math.round((basicPct - delta) * 10) / 10);
                    arr[basicIdx] = { ...arr[basicIdx], percent: nextBasic };
                }
            }
            return { ...f, salary_structure: arr };
        });
    };

    const addStructureRow = () => {
        // Auto-borrow 10% from Basic Salary so total stays at 100. If Basic doesn't have
        // 10% to spare, borrow whatever is available (down to 0). This keeps the sum
        // valid so the user can Save immediately after adding a row.
        setForm((f) => {
            const arr = [...(f.salary_structure || structure)];
            const basicIdx = arr.findIndex((r) => r.is_basic || r.key === "basic_salary");
            const basicPct = basicIdx >= 0 ? Number(arr[basicIdx].percent) || 0 : 0;
            const borrow = Math.min(10, Math.max(0, basicPct));
            if (basicIdx >= 0) {
                arr[basicIdx] = { ...arr[basicIdx], percent: Math.round((basicPct - borrow) * 10) / 10 };
            }
            arr.push({ key: "", label: "", percent: borrow, pensionable: false, taxable: true });
            return { ...f, salary_structure: arr };
        });
    };

    const removeStructureRow = (idx) => {
        // Return the removed row's percent back to Basic Salary so the total stays 100.
        setForm((f) => {
            const arr = [...(f.salary_structure || structure)];
            if (arr[idx]?.is_basic) return f;
            const returned = Number(arr[idx]?.percent) || 0;
            const basicIdx = arr.findIndex((r) => r.is_basic || r.key === "basic_salary");
            if (basicIdx >= 0) {
                const basicPct = Number(arr[basicIdx].percent) || 0;
                arr[basicIdx] = { ...arr[basicIdx], percent: Math.round((basicPct + returned) * 10) / 10 };
            }
            arr.splice(idx, 1);
            return { ...f, salary_structure: arr };
        });
    };

    const rebalanceFromBasic = () => {
        // Adjust Basic Salary so the total sums to exactly 100.
        setForm((f) => {
            const arr = [...(f.salary_structure || structure)];
            const basicIdx = arr.findIndex((r) => r.is_basic || r.key === "basic_salary");
            if (basicIdx < 0) return f;
            const others = arr.reduce((s, r, i) => i === basicIdx ? s : s + (Number(r.percent) || 0), 0);
            const newBasic = Math.max(0, Math.round((100 - others) * 10) / 10);
            arr[basicIdx] = { ...arr[basicIdx], percent: newBasic };
            return { ...f, salary_structure: arr };
        });
    };

    const resetSalaryDefaults = () => {
        setForm((f) => ({
            ...f,
            salary_structure: [
                { key: "basic_salary", label: "Basic Salary", percent: 60, pensionable: true, taxable: true, is_basic: true },
                { key: "housing", label: "Housing Allowance", percent: 25, pensionable: false, taxable: true },
                { key: "transport", label: "Transport Allowance", percent: 15, pensionable: false, taxable: true },
            ],
            basic_pct: 0.60, housing_pct: 0.25, transport_pct: 0.15,
        }));
    };

    const setCriterion = (idx, field, value) => {
        const arr = [...form.performance_criteria];
        arr[idx] = { ...arr[idx], [field]: field === "weight" ? Number(value) : value };
        if (field === "label" && !arr[idx].key) {
            arr[idx].key = String(value || "").toLowerCase().replace(/\s+/g, "_");
        }
        set("performance_criteria", arr);
    };

    const addCriterion = () => {
        const arr = [...(form.performance_criteria || [])];
        arr.push({ key: "", label: "", weight: 0, _uid: `c-${Date.now()}-${Math.random().toString(36).slice(2, 8)}` });
        set("performance_criteria", arr);
    };

    const removeCriterion = (idx) => {
        const arr = (form.performance_criteria || []).filter((_, i) => i !== idx);
        set("performance_criteria", arr);
    };

    const onSave = async (e) => {
        e?.preventDefault?.();
        if (!form.name) {
            toast.error("Company name is required");
            return;
        }
        if (Math.abs(totalWeight - 100) > 0.01) {
            toast.error(`Performance weights must sum to 100 (currently ${totalWeight}).`);
            return;
        }
        if (Math.abs(salaryTotalUi - 100) > 0.1) {
            toast.error(`Salary structure must sum to 100% (currently ${salaryTotalUi}%).`);
            return;
        }
        setSaving(true);
        try {
            // Derive legacy basic_pct/housing_pct/transport_pct from the dynamic structure
            // so any code paths that still read the fractions stay consistent.
            const payload = { ...form, salary_structure: structure };
            const bIdx = structure.findIndex((r) => r.is_basic || r.key === "basic_salary");
            const hIdx = structure.findIndex((r) => r.key === "housing");
            const tIdx = structure.findIndex((r) => r.key === "transport");
            if (bIdx >= 0) payload.basic_pct = Math.round((Number(structure[bIdx].percent) || 0) * 100) / 10000;
            if (hIdx >= 0) payload.housing_pct = Math.round((Number(structure[hIdx].percent) || 0) * 100) / 10000;
            if (tIdx >= 0) payload.transport_pct = Math.round((Number(structure[tIdx].percent) || 0) * 100) / 10000;
            const { data } = await api.put("/company-settings", payload);
            setForm(data);
            toast.success(data?.draft_payroll_items_updated
                ? `Company settings saved — ${data.draft_payroll_items_updated} draft payroll line(s) recalculated`
                : "Company settings saved");
            // If first-time configure and user came from onboarding, send them to dashboard
            const onboarding = window.location.pathname === "/settings" && new URLSearchParams(window.location.search).get("onboarding");
            if (onboarding) navigate("/dashboard");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    if (loading || !form) {
        return <div className="px-8 md:px-12 py-12 md:py-16 text-ink/40">Loading settings…</div>;
    }

    const isOnboarding = !form.is_configured;

    return (
        <form onSubmit={onSave} className="px-8 md:px-12 py-12 md:py-16" data-testid="settings-page">
            <div className="flex items-end justify-between flex-wrap gap-6">
                <div>
                    <div className="overline">{isOnboarding ? "First-time setup" : "Configuration"}</div>
                    <h1 className="mt-3 font-display font-black tracking-tighter text-4xl sm:text-5xl text-ink leading-[1.02]">
                        {isOnboarding ? "Tell us about your company" : "Company settings"}
                    </h1>
                    <p className="mt-3 text-sm text-ink/60 max-w-2xl">
                        {isOnboarding
                            ? "These details appear on payslips, settlement PDFs, and email signatures. You can always change them later."
                            : "These details appear on payslips, settlement PDFs, and email signatures."}
                    </p>
                </div>
                <button
                    type="submit"
                    disabled={saving}
                    className="inline-flex items-center gap-2 bg-klein text-white px-5 py-3 text-sm hover:bg-klein/90 disabled:opacity-60"
                    data-testid="settings-save-button"
                >
                    <Save className="w-4 h-4" />
                    {saving ? "Saving…" : isOnboarding ? "Finish setup" : "Save changes"}
                </button>
            </div>

            {!isOnboarding && <SignInLinkCard />}
            {!isOnboarding && <HolidaysEditor />}

            <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Identity */}
                <section className="lg:col-span-2 bg-white border border-ink/10 p-8" data-testid="settings-identity">
                    <div className="overline">Identity</div>
                    <h2 className="mt-1 font-display font-bold text-xl text-ink mb-6">Brand &amp; legal</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <Field label="Trading name *" span={2}>
                            <input value={form.name || ""} onChange={(e) => set("name", e.target.value)} className={inputCls} required data-testid="settings-name" />
                        </Field>
                        <Field label="Legal name">
                            <input value={form.legal_name || ""} onChange={(e) => set("legal_name", e.target.value)} className={inputCls} data-testid="settings-legal-name" />
                        </Field>
                        <Field label="Company Code" hint="Short prefix used in auto-generated Employee Numbers (e.g. EG → EG042601). Up to 6 letters/digits.">
                            <input
                                value={form.company_code || ""}
                                onChange={(e) => set("company_code", e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 6))}
                                className={inputCls + " font-mono tracking-wider"}
                                placeholder="e.g. EG"
                                maxLength={6}
                                data-testid="settings-company-code"
                            />
                        </Field>
                        <Field label="Country">
                            <input value={form.country || ""} onChange={(e) => set("country", e.target.value)} className={inputCls} data-testid="settings-country" />
                        </Field>
                        <Field label="RC number">
                            <input value={form.rc_number || ""} onChange={(e) => set("rc_number", e.target.value)} className={inputCls} data-testid="settings-rc" />
                        </Field>
                        <Field label="Tax ID (TIN)">
                            <input value={form.tin || ""} onChange={(e) => set("tin", e.target.value)} className={inputCls} data-testid="settings-tin" />
                        </Field>
                        <Field label="Phone">
                            <input value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} className={inputCls} data-testid="settings-phone" />
                        </Field>
                        <Field label="Public email" hint="Shown as 'Reply-To' on every outgoing email so replies land in your inbox.">
                            <div className="flex gap-2 items-stretch">
                                <input type="email" value={form.email || ""} onChange={(e) => set("email", e.target.value)} className={inputCls + " flex-1"} data-testid="settings-email" />
                                <TestEmailButton recipient={form.email} disabled={!form.email} />
                            </div>
                        </Field>
                        <Field label="Website">
                            <input value={form.website || ""} onChange={(e) => set("website", e.target.value)} className={inputCls} placeholder="https://" data-testid="settings-website" />
                        </Field>
                        <Field label="Payroll/HR contact email" hint="Shown in payslip footer.">
                            <input type="email" value={form.payroll_email || ""} onChange={(e) => set("payroll_email", e.target.value)} className={inputCls} data-testid="settings-payroll-email" />
                        </Field>
                        <Field label="Attendance summary on payslips" hint="Show working days, leave, absence and lateness at the bottom of every payslip.">
                            <label className="inline-flex items-center gap-2 text-sm text-ink/80 h-10 cursor-pointer">
                                <input type="checkbox" checked={form.payslip_show_attendance !== false} onChange={(e) => set("payslip_show_attendance", e.target.checked)} className="w-4 h-4 accent-klein" data-testid="settings-payslip-attendance" />
                                Show on payslips
                            </label>
                        </Field>
                        <Field label="Primary brand colour" hint="Auto-detected from logo. Used on PDF accents.">
                            <input type="color" value={form.primary_color || "#002FA7"} onChange={(e) => set("primary_color", e.target.value)} className="h-10 w-20 border border-ink/15 cursor-pointer" data-testid="settings-color" />
                        </Field>
                        <Field label="Address" span={2}>
                            <textarea rows="2" value={form.address || ""} onChange={(e) => set("address", e.target.value)} className={inputCls} data-testid="settings-address" />
                        </Field>
                        <Field label="City">
                            <input value={form.city || ""} onChange={(e) => set("city", e.target.value)} className={inputCls} data-testid="settings-city" />
                        </Field>
                        <Field label="State">
                            <input value={form.state || ""} onChange={(e) => set("state", e.target.value)} className={inputCls} data-testid="settings-state" />
                        </Field>
                    </div>
                </section>

                {/* Theme & Background — VISUAL */}

                {/* Logo + HR signature */}
                <section className="bg-white border border-ink/10 p-8" data-testid="settings-logo">
                    <div className="overline">Branding</div>
                    <h2 className="mt-1 font-display font-bold text-xl text-ink mb-6">Logo &amp; signatures</h2>

                    <label className="overline block mb-2">Company logo</label>
                    <div className="border-2 border-dashed border-ink/15 p-5 flex flex-col items-center justify-center text-center min-h-[140px] bg-bone/40">
                        {form.logo_data_url ? (
                            <>
                                <img src={form.logo_data_url} alt="Logo" className="max-h-20 max-w-full mb-3 object-contain" data-testid="settings-logo-preview" />
                                <button type="button" onClick={() => set("logo_data_url", "")} className="inline-flex items-center gap-1 text-xs text-destructive hover:underline" data-testid="settings-logo-remove">
                                    <X className="w-3 h-3" /> Remove
                                </button>
                            </>
                        ) : (
                            <>
                                <ImageIcon className="w-6 h-6 text-ink/30 mb-2" />
                                <p className="text-[11px] text-ink/50 mb-2">PNG, JPG, SVG. Max 800 KB.</p>
                            </>
                        )}
                        <label className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 border border-ink/20 text-[11px] uppercase tracking-[0.15em] cursor-pointer hover:bg-ink/5">
                            <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => onLogoUpload(e.target.files?.[0])}
                                data-testid="settings-logo-input"
                            />
                            {form.logo_data_url ? "Replace" : "Upload"}
                        </label>
                    </div>

                    <label className="overline block mt-6 mb-2">HR signature</label>
                    <div className="border-2 border-dashed border-ink/15 p-5 flex flex-col items-center justify-center text-center min-h-[140px] bg-bone/40">
                        {form.hr_signature_data_url ? (
                            <>
                                <img src={form.hr_signature_data_url} alt="HR signature" className="max-h-16 max-w-full mb-3 object-contain" data-testid="settings-sig-preview" />
                                <button type="button" onClick={() => set("hr_signature_data_url", "")} className="inline-flex items-center gap-1 text-xs text-destructive hover:underline" data-testid="settings-sig-remove">
                                    <X className="w-3 h-3" /> Remove
                                </button>
                            </>
                        ) : (
                            <>
                                <ImageIcon className="w-6 h-6 text-ink/30 mb-2" />
                                <p className="text-[11px] text-ink/50 mb-2">Used on payslips, settlements & letters.</p>
                            </>
                        )}
                        <label className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 border border-ink/20 text-[11px] uppercase tracking-[0.15em] cursor-pointer hover:bg-ink/5">
                            <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => onSignatureUpload(e.target.files?.[0])}
                                data-testid="settings-sig-input"
                            />
                            {form.hr_signature_data_url ? "Replace" : "Upload"}
                        </label>
                    </div>

                    <div className="mt-5 grid grid-cols-1 gap-4">
                        <Field label="Signatory name">
                            <input value={form.hr_signatory_name || ""} onChange={(e) => set("hr_signatory_name", e.target.value)} className={inputCls} placeholder="e.g. Adekunle Bello" data-testid="settings-sig-name" />
                        </Field>
                        <Field label="Signatory title">
                            <input value={form.hr_signatory_title || ""} onChange={(e) => set("hr_signatory_title", e.target.value)} className={inputCls} placeholder="Head of HR" data-testid="settings-sig-title" />
                        </Field>
                    </div>
                </section>

                {/* Letterhead — used on payslips + letters + settlement PDFs */}
                <section className="lg:col-span-3 bg-white border border-ink/10 p-8" data-testid="settings-letterhead">
                    <div className="overline">Documents</div>
                    <h2 className="mt-1 font-display font-bold text-xl text-ink mb-1">Letterhead</h2>
                    <p className="text-xs text-ink/50 mb-5">
                        Full-width header image used at the top of every letter and payslip PDF. Recommended dimensions: 1600 × 300 px, PNG/JPG.
                    </p>
                    <div className="flex items-start gap-6">
                        <div className="flex-1 bg-bone/40 border border-dashed border-ink/20 p-6 min-h-[120px] flex items-center justify-center">
                            {form.letterhead_data_url ? (
                                <img src={form.letterhead_data_url} alt="Letterhead" className="max-h-24 w-full object-contain" data-testid="settings-letterhead-preview" />
                            ) : (
                                <div className="text-center text-xs text-ink/40">No letterhead uploaded yet</div>
                            )}
                        </div>
                        <div className="flex flex-col gap-2 shrink-0">
                            <label className="inline-flex items-center gap-2 px-3 py-1.5 border border-ink/20 text-[11px] uppercase tracking-[0.15em] cursor-pointer hover:bg-ink/5">
                                <input type="file" accept="image/*" className="hidden" onChange={onLetterheadFile} data-testid="settings-letterhead-input" />
                                {form.letterhead_data_url ? "Replace" : "Upload"}
                            </label>
                            {form.letterhead_data_url && (
                                <button type="button" onClick={() => set("letterhead_data_url", "")} className="text-[11px] text-destructive hover:underline" data-testid="settings-letterhead-remove">
                                    Remove
                                </button>
                            )}
                        </div>
                        <div className="mt-5 pt-5 border-t border-ink/10 flex flex-wrap items-center gap-4">
                            <div className="min-w-[140px]">
                                <div className="overline mb-1.5">Banner height</div>
                                <p className="text-[11px] text-ink/50 mb-2">Controls how tall the letterhead / fallback strip appears at the top of every PDF.</p>
                            </div>
                            <div className="flex flex-wrap items-center gap-2" role="radiogroup" aria-label="Banner height">
                                {[
                                    { v: 30, label: "Compact · 30 mm" },
                                    { v: 40, label: "Standard · 40 mm" },
                                    { v: 55, label: "Tall · 55 mm" },
                                ].map((opt) => {
                                    const active = Number(form.letterhead_banner_height_mm || 30) === opt.v;
                                    return (
                                        <button
                                            key={opt.v}
                                            type="button"
                                            onClick={() => set("letterhead_banner_height_mm", opt.v)}
                                            className={`text-[11px] uppercase tracking-[0.12em] px-3 py-1.5 border ${active ? "bg-klein text-white border-klein" : "border-ink/20 text-ink/70 hover:bg-ink/5"}`}
                                            data-testid={`settings-letterhead-height-${opt.v}`}
                                            aria-pressed={active}
                                        >{opt.label}</button>
                                    );
                                })}
                                <input
                                    type="number" min="18" max="70"
                                    value={form.letterhead_banner_height_mm ?? 30}
                                    onChange={(e) => set("letterhead_banner_height_mm", Math.max(18, Math.min(70, Number(e.target.value) || 30)))}
                                    className="w-20 text-sm border border-ink/15 px-2 py-1.5 focus:outline-none focus:border-klein"
                                    data-testid="settings-letterhead-height-input"
                                    aria-label="Custom banner height in millimetres"
                                />
                                <span className="text-xs text-ink/50">mm</span>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Work hours + Lateness policy */}
                <section className="lg:col-span-3 bg-white border border-ink/10 p-8" data-testid="settings-work-hours">
                    <div className="overline">Attendance</div>
                    <h2 className="mt-1 font-display font-bold text-xl text-ink mb-1">Work hours &amp; lateness policy</h2>
                    <p className="text-xs text-ink/50 mb-5">
                        Defines your official work day plus how lateness is penalised on the payslip.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
                        <Field label="Resumption time">
                            <input type="time" value={form.work_start_time || "09:00"} onChange={(e) => set("work_start_time", e.target.value)} className={inputCls} data-testid="settings-work-start" />
                        </Field>
                        <Field label="Closing time">
                            <input type="time" value={form.work_end_time || "17:00"} onChange={(e) => set("work_end_time", e.target.value)} className={inputCls} data-testid="settings-work-end" />
                        </Field>
                        <Field label="Grace period (minutes)" hint="Tolerated late minutes before penalty">
                            <input type="number" min="0" max="120" value={form.grace_minutes ?? 10} onChange={(e) => set("grace_minutes", Number(e.target.value) || 0)} className={inputCls} data-testid="settings-grace-minutes" />
                        </Field>
                        <Field label="Lateness penalty">
                            <select value={form.lateness_mode || "none"} onChange={(e) => set("lateness_mode", e.target.value)} className={inputCls + " bg-transparent"} data-testid="settings-lateness-mode">
                                <option value="none">None</option>
                                <option value="fixed">Fixed ₦ per event</option>
                                <option value="daily_rate">% of daily salary</option>
                            </select>
                        </Field>
                        {form.lateness_mode === "fixed" && (
                            <Field label="Fixed amount per lateness event (₦)" testid="settings-lateness-amount-wrap">
                                <MoneyInput value={form.lateness_amount || 0} onChange={(e) => set("lateness_amount", Number(e.target.value) || 0)} className={inputCls} data-testid="settings-lateness-amount" />
                            </Field>
                        )}
                        {form.lateness_mode === "daily_rate" && (
                            <Field label="% of one day's salary per lateness event" testid="settings-lateness-rate-wrap">
                                <input type="number" min="0" max="100" step="any" value={form.lateness_daily_rate_pct || 0} onChange={(e) => set("lateness_daily_rate_pct", Number(e.target.value) || 0)} className={inputCls} data-testid="settings-lateness-rate" />
                            </Field>
                        )}
                    </div>
                </section>

                {/* Bank */}
                <section className="lg:col-span-2 bg-white border border-ink/10 p-8" data-testid="settings-bank">
                    <div className="overline">Banking</div>
                    <h2 className="mt-1 font-display font-bold text-xl text-ink mb-6">Disbursement source</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                        <Field label="Bank name">
                            <input value={form.bank_name || ""} onChange={(e) => set("bank_name", e.target.value)} className={inputCls} data-testid="settings-bank-name" />
                        </Field>
                        <Field label="Account number">
                            <input value={form.bank_account_number || ""} onChange={(e) => set("bank_account_number", e.target.value)} className={inputCls} data-testid="settings-bank-account" />
                        </Field>
                        <Field label="Account name">
                            <input value={form.bank_account_name || ""} onChange={(e) => set("bank_account_name", e.target.value)} className={inputCls} data-testid="settings-bank-account-name" />
                        </Field>
                    </div>
                </section>

                {/* Salary Structure — DYNAMIC expandable list of allowances */}
                <section className="lg:col-span-3 bg-white border border-ink/10 p-8" data-testid="settings-salary-structure">
                    <div className="flex items-end justify-between flex-wrap gap-3">
                        <div>
                            <div className="overline">Payroll</div>
                            <h2 className="mt-1 font-display font-bold text-xl text-ink">Salary structure</h2>
                            <p className="text-xs text-ink/50 mt-1 max-w-xl">
                                Split each employee&apos;s monthly gross into any number of allowances. The <b>Basic Salary</b> row
                                is used for pension contribution (8% of basic). Every row here also appears as its own line on the payslip.
                            </p>
                        </div>
                        <div className="flex items-center gap-3 flex-wrap">
                            <span
                                className={`text-xs font-medium px-2.5 py-1 ${Math.abs(salaryTotalUi - 100) < 0.1 ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}
                                data-testid="settings-salary-total"
                            >
                                Total: {salaryTotalUi.toFixed(1)}%
                            </span>
                            {Math.abs(salaryTotalUi - 100) > 0.1 && (
                                <button
                                    type="button"
                                    onClick={rebalanceFromBasic}
                                    className="inline-flex items-center gap-1 px-3 py-2 text-xs uppercase tracking-[0.15em] bg-amber-500 text-white hover:bg-amber-600"
                                    data-testid="settings-salary-rebalance"
                                    title="Adjust Basic Salary so all rows sum to exactly 100%"
                                >
                                    <Wand2 className="w-3.5 h-3.5" /> Rebalance
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={addStructureRow}
                                className="inline-flex items-center gap-1 px-3 py-2 text-xs uppercase tracking-[0.15em] bg-klein text-white hover:bg-klein/90"
                                data-testid="settings-salary-add"
                            >
                                <Plus className="w-3.5 h-3.5" /> Add allowance
                            </button>
                            <button
                                type="button"
                                onClick={resetSalaryDefaults}
                                className="inline-flex items-center gap-1 px-3 py-2 text-xs uppercase tracking-[0.15em] border border-ink/15 hover:bg-ink/5"
                                data-testid="settings-salary-defaults"
                                title="Reset to statutory 60/25/15"
                            >
                                <Wand2 className="w-3.5 h-3.5" /> Defaults
                            </button>
                        </div>
                    </div>
                    <div className="mt-5 space-y-2" data-testid="settings-salary-rows">
                        <div className="hidden md:grid grid-cols-[1fr,120px,140px,140px,40px] gap-3 items-end pb-1 text-[10px] tracking-widest uppercase text-ink/50">
                            <span>Allowance name</span>
                            <span className="text-right">Percent</span>
                            <span className="text-center">Pensionable</span>
                            <span className="text-center">Taxable</span>
                            <span></span>
                        </div>
                        {structure.map((row, idx) => (
                            <div key={idx} className="grid grid-cols-[1fr,120px,140px,140px,40px] gap-3 items-center" data-testid={`settings-salary-row-${idx}`}>
                                <div className="min-w-0">
                                    <input
                                        type="text"
                                        value={row.label || ""}
                                        onChange={(e) => setStructureRow(idx, "label", e.target.value)}
                                        placeholder={row.is_basic ? "Basic Salary" : "e.g. Meal Allowance"}
                                        disabled={!!row.is_basic}
                                        className={`${inputCls} ${row.is_basic ? "bg-ink/5 text-ink/70" : ""}`}
                                        data-testid={`settings-salary-label-${idx}`}
                                    />
                                </div>
                                <div className="flex items-center gap-1">
                                    <input
                                        type="number"
                                        min="0"
                                        max="100"
                                        step="any"
                                        value={row.percent ?? 0}
                                        onChange={(e) => setStructureRow(idx, "percent", e.target.value)}
                                        className={`${inputCls} text-right`}
                                        data-testid={`settings-salary-pct-${idx}`}
                                    />
                                    <span className="text-xs text-ink/50">%</span>
                                </div>
                                <label className="flex items-center justify-center gap-2 text-xs text-ink/70">
                                    <input
                                        type="checkbox"
                                        checked={!!row.pensionable}
                                        onChange={(e) => setStructureRow(idx, "pensionable", e.target.checked)}
                                        disabled={!!row.is_basic}
                                        data-testid={`settings-salary-pensionable-${idx}`}
                                    />
                                    <span className="hidden md:inline">Pensionable</span>
                                </label>
                                <label className="flex items-center justify-center gap-2 text-xs text-ink/70">
                                    <input
                                        type="checkbox"
                                        checked={row.taxable !== false}
                                        onChange={(e) => setStructureRow(idx, "taxable", e.target.checked)}
                                        data-testid={`settings-salary-taxable-${idx}`}
                                    />
                                    <span className="hidden md:inline">Taxable</span>
                                </label>
                                {row.is_basic ? (
                                    <span className="text-ink/25 text-xs text-center" title="Basic Salary is required">·</span>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={() => removeStructureRow(idx)}
                                        className="text-red-600 hover:text-red-800 flex items-center justify-center"
                                        aria-label={`Remove ${row.label || "row"}`}
                                        data-testid={`settings-salary-remove-${idx}`}
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                    {Math.abs(salaryTotalUi - 100) > 0.1 && (
                        <p className="mt-3 text-xs text-red-600" data-testid="settings-salary-warning">
                            Percentages must sum to 100% before you can save (currently {salaryTotalUi.toFixed(1)}%). Click <b>Rebalance</b> to auto-adjust Basic Salary, or edit the rows manually.
                        </p>
                    )}
                    <p className="mt-4 text-[11px] text-ink/40">
                        Tip: adding or editing an allowance now automatically deducts from Basic Salary so the total stays at 100%.
                        Changes apply to <strong>future payroll runs</strong> and newly generated letters. Existing runs are not retroactively updated.
                    </p>

                    {/* Extra salary components — admin-defined breakdown rows */}
                    <div className="mt-8 pt-6 border-t border-ink/10" data-testid="settings-extra-components">
                        <div className="flex items-end justify-between flex-wrap gap-2 mb-3">
                            <div>
                                <div className="overline">Custom rows</div>
                                <h3 className="mt-1 font-display font-bold text-base text-ink">Extra salary components</h3>
                                <p className="text-xs text-ink/50 mt-1 max-w-xl">
                                    Add company-policy allowances (e.g. Lunch, Transport top-up, Housing top-up). Each row appears on every payslip on top of the statutory Basic / Housing / Transport split.
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setForm((f) => ({
                                    ...f,
                                    extra_salary_components: [
                                        ...(f.extra_salary_components || []),
                                        { id: `extra_${Date.now()}`, label: "", kind: "fixed", value: 0, of: "basic", taxable: true, pensionable: false },
                                    ],
                                }))}
                                className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.15em] px-3 py-2 border border-klein text-klein hover:bg-klein hover:text-white"
                                data-testid="settings-extra-add"
                            >
                                + Add row
                            </button>
                        </div>
                        {(form?.extra_salary_components || []).length === 0 ? (
                            <p className="text-xs text-ink/40 py-4 text-center border border-dashed border-ink/15">No custom rows. Statutory 60/25/15 only.</p>
                        ) : (
                            <div className="space-y-2">
                                {(form.extra_salary_components || []).map((row, idx) => (
                                    <div key={row.id || idx} className="grid grid-cols-12 gap-2 items-end" data-testid={`settings-extra-row-${idx}`}>
                                        <div className="col-span-12 md:col-span-3">
                                            <label className="overline block mb-1 text-[10px]">Label</label>
                                            <input
                                                value={row.label}
                                                onChange={(e) => setExtra(idx, "label", e.target.value)}
                                                placeholder="e.g. Lunch allowance"
                                                className={inputCls}
                                                data-testid={`settings-extra-label-${idx}`}
                                            />
                                        </div>
                                        <div className="col-span-6 md:col-span-2">
                                            <label className="overline block mb-1 text-[10px]">Kind</label>
                                            <select value={row.kind} onChange={(e) => setExtra(idx, "kind", e.target.value)} className={inputCls} data-testid={`settings-extra-kind-${idx}`}>
                                                <option value="fixed">Fixed ₦</option>
                                                <option value="percent">% of</option>
                                            </select>
                                        </div>
                                        <div className="col-span-6 md:col-span-2">
                                            <label className="overline block mb-1 text-[10px]">Value</label>
                                            <input
                                                type="number"
                                                min="0"
                                                step="any"
                                                value={row.value ?? 0}
                                                onChange={(e) => setExtra(idx, "value", Number(e.target.value) || 0)}
                                                className={inputCls}
                                                data-testid={`settings-extra-value-${idx}`}
                                            />
                                        </div>
                                        <div className="col-span-6 md:col-span-2">
                                            <label className="overline block mb-1 text-[10px]">Of</label>
                                            <select value={row.of} onChange={(e) => setExtra(idx, "of", e.target.value)} className={inputCls} disabled={row.kind === "fixed"} data-testid={`settings-extra-of-${idx}`}>
                                                <option value="basic">Basic</option>
                                                <option value="gross">Gross</option>
                                            </select>
                                        </div>
                                        <div className="col-span-12 md:col-span-2 flex items-end gap-3 pt-1">
                                            <label className="inline-flex items-center gap-1 text-[11px] text-ink/70 cursor-pointer">
                                                <input type="checkbox" checked={!!row.taxable} onChange={(e) => setExtra(idx, "taxable", e.target.checked)} className="w-3 h-3 accent-klein" data-testid={`settings-extra-taxable-${idx}`} />
                                                Taxable
                                            </label>
                                            <label className="inline-flex items-center gap-1 text-[11px] text-ink/70 cursor-pointer">
                                                <input type="checkbox" checked={!!row.pensionable} onChange={(e) => setExtra(idx, "pensionable", e.target.checked)} className="w-3 h-3 accent-klein" data-testid={`settings-extra-pensionable-${idx}`} />
                                                Pensionable
                                            </label>
                                        </div>
                                        <div className="col-span-12 md:col-span-1">
                                            <button
                                                type="button"
                                                onClick={() => setForm((f) => ({
                                                    ...f,
                                                    extra_salary_components: f.extra_salary_components.filter((_, i) => i !== idx),
                                                }))}
                                                className="w-full py-2 text-xs text-red-600 hover:bg-red-50"
                                                data-testid={`settings-extra-remove-${idx}`}
                                            >
                                                Remove
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Statutory IDs printed on PAYE / Pension reports */}
                    <div className="mt-8 pt-6 border-t border-ink/10 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="settings-statutory-ids">
                        <Field label="Employer Pension Code (PFC)" hint="Printed on pension schedule">
                            <input
                                value={form?.employer_pension_code || ""}
                                onChange={(e) => set("employer_pension_code", e.target.value)}
                                placeholder="e.g. PEN-12345"
                                className={inputCls}
                                data-testid="settings-employer-pension-code"
                            />
                        </Field>
                        <Field label="Employer Tax ID" hint="State IRS employer code (PAYE)">
                            <input
                                value={form?.employer_tax_id || ""}
                                onChange={(e) => set("employer_tax_id", e.target.value)}
                                placeholder="e.g. LIRS-001"
                                className={inputCls}
                                data-testid="settings-employer-tax-id"
                            />
                        </Field>
                        <Field label="Pension Provider" hint="Default PFA for new employees">
                            <select
                                value={form?.pension_provider || ""}
                                onChange={(e) => set("pension_provider", e.target.value)}
                                className={inputCls + " bg-transparent"}
                                data-testid="settings-pension-provider"
                            >
                                <option value="">— Select PFA —</option>
                                {(() => { const l = form?.pfa_list?.length ? form.pfa_list : (official.pfas.length ? official.pfas : pfaList); const cur = form?.pension_provider; return cur && !l.includes(cur) ? [cur, ...l] : l; })().map((p) => (
                                    <option key={p} value={p}>{p}</option>
                                ))}
                            </select>
                        </Field>
                    </div>
                </section>

                {/* Banks & PFAs — editable dropdown lists for employee records */}
                <section className="lg:col-span-3 bg-white border border-ink/10 p-8" data-testid="settings-reference-lists">
                    <div className="overline">Employee dropdowns</div>
                    <h2 className="mt-1 font-display font-bold text-xl text-ink">Banks, PFAs, HMOs &amp; Divisions</h2>
                    <p className="text-xs text-ink/50 mt-1">Pre-loaded with the latest CBN and PenCom licensed lists and common Nigerian HMOs. Add or remove names, then save settings.</p>
                    <div className="mt-6 grid md:grid-cols-2 xl:grid-cols-4 gap-10">
                        <ReferenceListEditor
                            title="Banks"
                            hint="Shown in the employee bank field"
                            items={form?.bank_list || []}
                            official={official.banks}
                            onChange={(v) => set("bank_list", v)}
                            testid="settings-bank-list"
                        />
                        <ReferenceListEditor
                            title="PFAs"
                            hint="Shown in the employee PFA field"
                            items={form?.pfa_list || []}
                            official={official.pfas}
                            onChange={(v) => set("pfa_list", v)}
                            testid="settings-pfa-list"
                        />
                        <ReferenceListEditor
                            title="HMO providers"
                            hint="Shown in the employee HMO provider field"
                            items={form?.hmo_list || []}
                            official={official.hmos}
                            emptyLabel="common HMO list"
                            resetLabel="Reset to common list"
                            onChange={(v) => set("hmo_list", v)}
                            testid="settings-hmo-list"
                        />
                        <ReferenceListEditor
                            title="Divisions"
                            hint="The only divisions staff can be assigned to"
                            items={form?.division_list || []}
                            official={[]}
                            emptyLabel="not set up yet"
                            resetLabel="Clear list"
                            onChange={(v) => set("division_list", v)}
                            testid="settings-division-list"
                        />
                    </div>
                </section>

                {/* Performance criteria */}
                <section className="lg:col-span-3 bg-white border border-ink/10 p-8" data-testid="settings-perf">
                    <div className="flex items-end justify-between flex-wrap gap-3">
                        <div>
                            <div className="overline">Performance evaluation</div>
                            <h2 className="mt-1 font-display font-bold text-xl text-ink">Scoring criteria</h2>
                            <p className="text-xs text-ink/50 mt-1">Used by managers when evaluating staff. Weights must sum to 100%.</p>
                        </div>
                        <div className="flex items-center gap-3 flex-wrap">
                            <span className={`text-xs font-medium px-2.5 py-1 ${Math.abs(totalWeight - 100) < 0.01 ? "bg-green-50 text-green-700" : "bg-yellow-50 text-yellow-700"}`} data-testid="settings-perf-total">
                                Total: {totalWeight.toFixed(2)}%
                            </span>
                            <button type="button" onClick={addCriterion} className="inline-flex items-center gap-1 px-3 py-2 text-xs uppercase tracking-[0.15em] border border-ink/15 hover:bg-ink/5" data-testid="settings-add-criterion">
                                <Plus className="w-3.5 h-3.5" /> Add
                            </button>
                            <button
                                type="button"
                                onClick={() => set("performance_criteria", [
                                    { key: "kpi", label: "KPI achievement", weight: 30 },
                                    { key: "productivity", label: "Productivity", weight: 25 },
                                    { key: "teamwork", label: "Teamwork & collaboration", weight: 15 },
                                    { key: "punctuality", label: "Punctuality & attendance", weight: 15 },
                                    { key: "quality", label: "Quality of work", weight: 15 },
                                ])}
                                className="inline-flex items-center gap-1 px-3 py-2 text-xs uppercase tracking-[0.15em] border border-ink/15 hover:bg-ink/5"
                                data-testid="settings-reset-criteria"
                                title="Reset to default 5-metric criteria"
                            >
                                <Wand2 className="w-3.5 h-3.5" /> Defaults
                            </button>
                        </div>
                    </div>
                    <div className="mt-5 space-y-2" data-testid="settings-criteria-list">
                        {(form.performance_criteria || []).map((c, idx) => (
                            <div key={c._uid || c.key || `crit-${idx}`} className="grid grid-cols-12 gap-3 items-center" data-testid={`settings-criterion-${idx}`}>
                                <input value={c.label || ""} onChange={(e) => setCriterion(idx, "label", e.target.value)} placeholder="Criterion label" className={`col-span-12 md:col-span-7 ${inputCls}`} />
                                <div className="col-span-9 md:col-span-4 flex items-center gap-2">
                                    <input
                                        type="number"
                                        min="0"
                                        max="100"
                                        step="0.5"
                                        value={c.weight}
                                        onChange={(e) => setCriterion(idx, "weight", e.target.value)}
                                        className={inputCls}
                                    />
                                    <span className="text-xs text-ink/50">%</span>
                                </div>
                                <button type="button" onClick={() => removeCriterion(idx)} className="col-span-3 md:col-span-1 p-2 text-ink/50 hover:text-destructive flex justify-center" title="Remove criterion">
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        ))}
                    </div>
                    <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-5">
                        <Field label="Score scale" hint="Each criterion is scored from 0 to this number.">
                            <input
                                type="number"
                                min="1"
                                max="100"
                                value={form.performance_max_score || 10}
                                onChange={(e) => set("performance_max_score", Number(e.target.value))}
                                className={inputCls}
                                data-testid="settings-perf-max-score"
                            />
                        </Field>
                    </div>
                </section>
            </div>
        </form>
    );
}


function TestEmailButton({ recipient, disabled }) {
    const [sending, setSending] = useState(false);
    const onSend = async () => {
        if (!recipient) return;
        setSending(true);
        try {
            const { data } = await api.post("/notifications/test-email", { to: recipient, subject: "Verify your HRM email branding" });
            if (data?.ok) {
                toast.success(`Sent to ${data.to}. Check your inbox — sender should show your company name.`);
            } else {
                toast.error("Resend rejected the send. Check that RESEND_API_KEY is configured on the server.");
            }
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not send test email");
        } finally {
            setSending(false);
        }
    };
    return (
        <button
            type="button"
            onClick={onSend}
            disabled={disabled || sending}
            title={disabled ? "Save a public email first" : `Send a test email to ${recipient}`}
            className="inline-flex items-center gap-1.5 px-3 border border-klein text-klein text-xs uppercase tracking-[0.12em] font-medium hover:bg-klein hover:text-white disabled:opacity-40 disabled:cursor-not-allowed transition rounded whitespace-nowrap"
            data-testid="settings-test-email-btn"
        >
            {sending ? <Mail className="w-3 h-3 animate-pulse" /> : <Send className="w-3 h-3" />}
            {sending ? "Sending…" : "Send test"}
        </button>
    );
}
